import { history } from "../App";
import { StoreMain } from "./StoreMain";
import { StoreWorkbooks } from "./StoreWorkbooks";
import { StoreElements } from "./StoreElements";
import { StoreAppSync } from "./StoreAppSync";
import { StoreCalculation } from "./StoreCalculation";
import { StoreTables } from "./StoreTables";
import { StoreFiles } from "./StoreFiles";
import { StoreSharing } from "./StoreSharing";
import { StoreTeams } from "./StoreTeams";
import { StoreCache } from "./StoreCache";
import { StoreAI } from "./StoreAI";
import { StoreServices } from "./StoreServices";
import { colors } from "../scss/colors";
import {
  isMobileOnly,
  isIOS,
  isSafari,
  isMacOs,
  getUA,
} from "react-device-detect";
import isPast from "date-fns/isPast";
import { detectIncognito } from "detect-incognito";
import * as microsoftTeams from "@microsoft/teams-js";

class RootStore {
  isPhone = isMobileOnly;
  isIOS = isIOS;
  isEmbedded = window.location !== window.parent.location;
  isSafari =
    isSafari || (isMacOs && window.location !== window.parent.location);
  isMacOs = isMacOs;
  isElectron = window.todesktop;
  isTeams = this.checkInTeams();
  isIncognitoBrowser = false;
  isStandalone = false;
  isDebug = false;
  activeDevice = "";
  colors = colors;
  encryptionVersion = "";
  default = {
    padding: 20,
    margin: 20,
    cardListFontSize: this.isPhone ? 16 : 14,
    cardMainFontSize: this.isPhone ? 16 : 14,
    cardContentFontSize: this.isPhone ? 16 : 14,
    fontSize: 16,
    fontFamily: "ProximaSoft", //"Lato",
  };
  microsoftAccessToken = undefined;
  microsoftAccessTokenType = undefined;
  manifestId = undefined;

  constructor() {
    let isDebug = process.env.NODE_ENV !== "production";
    this.checkIsStandalone();

    this.main = new StoreMain(this);
    this.teams = new StoreTeams(this);
    this.workbooks = new StoreWorkbooks(this);
    this.elements = new StoreElements(this);
    this.appsync = new StoreAppSync(this);
    this.calculation = new StoreCalculation(this);
    this.tables = new StoreTables(this);
    this.files = new StoreFiles(this);
    this.sharing = new StoreSharing(this);
    this.cache = new StoreCache(this);
    this.services = new StoreServices(this);
    this.ai = new StoreAI(this);
    if (this.isElectron) {
      this.colors.headerBackgroundColor = "#2D3F4F";
      this.colors.headerTextColor = "white";
      this.colors.headerLogoBackground = "#2D3F4F";
    }
    if (window?.location?.origin) {
      if (window.location.origin.includes("staging.")) isDebug = true;
    }
    this.isDebug = isDebug;
    console.log("INFO isDebug", this.isDebug);

    if (this.isEmbedded) {
      console.log(
        "INFO isEmbedded",
        this.isEmbedded,
        window.parent,
        window.parent.location,
      );
    }
    // detect the device
    try {
      !this.isSafari &&
        detectIncognito().then((result) => {
          if (result.isPrivate) this.isIncognitoBrowser = true;
        });
      this.activeDevice = getUA;
    } catch (error) {
      console.log("ERROR detect device", error);
    }
  }

  beep() {
    try {
      navigator.vibrate(200);
    } catch (e) {}
  }

  // =================================================================
  // Navigation
  // =================================================================

  reloadPage() {
    window.location.reload(false);
  }

  openUrl(targetUrl, reload) {
    if (targetUrl === "/files") {
      const isFiles = window.location.hash.includes("/files");
      if (isFiles) {
        history.push("/temp");
        history.goBack();
        return;
      }
    }

    history.push(targetUrl);
    if (reload) this.reloadPage();
  }

  openFileId(fileId) {
    this.openUrl("/file/" + fileId);
  }

  // =================================================================
  // Memory management
  // =================================================================

  cleanStores(view) {
    if (view !== "file") this.files.closeFile();
    if (view !== "table") this.tables.clearActiveTableData();
  }

  // =================================================================
  // ENVIRONMENT
  // =================================================================

  checkInTeams() {
    // eslint-disable-next-line dot-notation
    const microsoftTeamsLib = microsoftTeams || window["microsoftTeams"];

    if (!microsoftTeamsLib) {
      return false; // the Microsoft Teams library is for some reason not loaded
    }

    // parent = teams
    if (
      (window.parent === window.self && window.nativeInterface) ||
      window.name === "embedded-page-container" ||
      window.name === "extension-tab-frame"
    ) {
      return true;
    }

    // parent = microsoft 365, outlook
    if (window.location.ancestorOrigins?.length > 0) {
      const ancestorOrigin = window.location.ancestorOrigins[0];
      if (ancestorOrigin?.includes("microsoft365.com")) {
        console.log("INFO microsoft 365", ancestorOrigin);
        return true;
      }
      if (
        ancestorOrigin?.includes("office.com") ||
        ancestorOrigin?.includes("outlook")
      ) {
        console.log("INFO microsoft outlook", ancestorOrigin);
        return true;
      }
    }
    return false;
  }

  checkIsStandalone() {
    if (this.isStandalone) return true;

    // detect progressier
    let isStandalone = window.navigator.standalone === true;

    if (!isStandalone) {
      isStandalone = document
        .querySelector("body")
        .classList.contains("progressier-standalone");
    }
    if (!isStandalone) {
      if (window.matchMedia("(display-mode: standalone)").matches) {
        isStandalone = true;
      }
    }
    console.log("INFO isStandalone", isStandalone);
    this.isStandalone = isStandalone;
    sessionStorage.setItem("isStandalone-root", isStandalone);
  }

  // =================================================================
  // MANIFEST
  // =================================================================

  async setManifestId(manifestId) {
    if (this.manifestId || this.isDebug) return;
    if (!manifestId) manifestId = "HGRUr2rqt9wk4I4eFab6"; // default

    try {
      const script = document.createElement("script");
      script.setAttribute(
        "src",
        `https://progressier.app/${manifestId}/script.js`,
      );
      script.setAttribute("defer", "true");
      document.querySelector("head").appendChild(script);
      console.log("INFO setManifestId", manifestId);

      this.manifestId = manifestId;
    } catch (error) {
      console.log("ERROR setManifestId", error);
    }
  }

  // =================================================================
  // ACCESS TOKENS
  // =================================================================

  async setAccessToken(token, type, connector) {
    if (!token) return;

    const lowercasedScopes = token.scopes.map(function (name) {
      return name.toLowerCase();
    });

    const accessToken = {
      accessToken: token.accessToken,
      scopes: lowercasedScopes,
      tenantId: token.tenantId,
      expiresOn: token.expiresOn,
      accountUserName: token.account?.name,
      accountUserEmail: token.account?.username,
    };
    this.microsoftAccessToken = accessToken;
    this.microsoftAccessTokenType = type;
    console.log("INFO setAccessToken", type, connector);
  }

  getAccessTokenScopes() {
    return this.microsoftAccessToken?.scopes;
  }

  getIsAccessTokenScopeSupported(scope) {
    if (!this.microsoftAccessToken) return false;

    const isOK = this.microsoftAccessToken.scopes?.includes(scope);
    return isOK;
  }

  getHasUserFileAccess() {
    return (
      this.getIsAccessTokenScopeSupported("files.readwrite.all") ||
      this.getIsAccessTokenScopeSupported("files.readwrite")
    );
  }

  getHasUserEmailSendRight() {
    return this.getIsAccessTokenScopeSupported("mail.send");
  }

  getIsAccessTokenStillValid() {
    if (!this.microsoftAccessToken) return false;

    const expires = this.microsoftAccessToken.expiresOn;
    const expired = isPast(expires);
    return expired === false;
  }
}

const store = new RootStore();

export default store;
